<script>
// Host global de modais — desacoplado da navbar.
// Renderizado UMA vez no main.vue (fora do switch v1/v2 da navegação) para que
// cada modal continue se auto-inscrevendo no GlobalBus independentemente de qual
// shell de navegação está ativo. Sem isso, ao trocar para a navegação v2 a
// navbar antiga sai do DOM e os GlobalBus.$emit(MODAL.*) disparam sem ouvinte.
//
// PERFORMANCE: os modais são carregados como ASYNC COMPONENTS (() => import) e
// agrupados em chunks webpack ("global-modals" e, para os do CRM, o mais pesado
// "global-modals-crm"), saindo do bundle inicial —
// o boot da app não paga o parse de ~60 modais no caminho crítico. Comportamento
// idêntico: cada modal segue montado e assinando o GlobalBus no created(); só o
// download/parse do código sai do bundle principal.
// EXCEÇÕES eager: modais disparados por GlobalBus ANTES de uma interação do
// usuário — async correria o risco de o chunk chegar depois do $emit e o modal
// não abrir. Por isso ficam no bundle inicial:
//   - ModalConsentirCrawlerDiario: disparado no LOAD (services/parameters.js,
//     fluxo IS_CLIENTE).
//   - ModalExtensionToken: disparado no mounted() de extension-auth.vue (deep-link
//     /extension-auth aberto pela extensão Chrome) via setTimeout(150ms) — janela
//     curta demais para um download de chunk em cold boot.
//   - TranscriptDetailModal: disparado no mounted() de view-crm-transcricoes-history.vue
//     (deep-link ?transcricao=<uuid> do CTA "Ver transcrição completa" do email de
//     resumo) — mesmo racional do ModalExtensionToken: async chunk chegando depois
//     do $emit faz o evento se perder (GlobalBus não enfileira) e o modal nunca abre.
import { MODAL } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import { atualizarClienteNoRelacionamentoOtimista } from '@modules/crm/controller/shared/relationship/relationship'
import { atualizarClienteNosLeads } from '@modules/crm/controller/shared/leads/leads'

import ModalConsentirCrawlerDiario from '@modal/consentir-crawler-diario'
import ModalExtensionToken from '@modules/crm/modal/extension-token-modal/extension-token-modal'
import ModalWhatsappConectar from '@modules/aawz-intelligence-v2/modal/whatsapp-conectar/whatsapp-conectar.vue'
import TranscriptDetailModal from '@modules/crm/modal/transcript-detail-modal/transcript-detail-modal.vue'

// Mesmo factory registrado em `components.TaskDetailModal` abaixo, hoistado para
// que o replay (ver `replayTaskDetailWhenChunkArrives`) possa aguardar o MESMO
// chunk webpack — a segunda chamada reusa a promise já em voo/cacheada.
const loadTaskDetailModal = () =>
  import(
    /* webpackChunkName: "global-modals-crm" */ '@modules/tarefas/modal/task-detail-modal/task-detail-modal.vue'
  )

// Mesmo racional do `loadTaskDetailModal` acima, hoistados para os 4 modais do
// CRM alcançáveis pela busca global (Ctrl/Cmd+K) a partir de QUALQUER página —
// não só de dentro do módulo CRM. Ver `replayCrmModalWhenChunkArrives`: em cache
// frio, a ação da busca navega e já emite `GlobalBus.$emit(MODAL.X, payload)`
// antes do chunk "global-modals-crm" (o mais pesado) terminar de baixar/montar
// o modal real — sem replay, o emit se perde para sempre (GlobalBus não
// enfileira) e o modal nunca abre. Sintoma reproduzido em QA: rota muda, modal
// não.
const loadCrmNewLeadModal = () =>
  import(
    /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-add-new-lead/crm-add-new-lead.vue'
  )
const loadCrmAddClientModal = () =>
  import(
    /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-add-client/crm-add-client.vue'
  )
const loadCrmImportLeadsBatchModal = () =>
  import(
    /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-import-leads-batch/crm-import-leads-batch.vue'
  )
const loadTranscriptProjectModal = () =>
  import(
    /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/transcript-project-modal/transcript-project-modal.vue'
  )
// "Criar oportunidade" e "Agendar tarefa" (ações do palette sem `to`/com `to`
// respectivamente) também disparam `GlobalBus.$emit` de fora do módulo CRM —
// mesmo risco de chunk frio dos 4 acima. `ModalCrmOpportunities` e
// `CrmScheduleTaskCalendar` já viviam no chunk "global-modals-crm" (satélites
// do crm-lead-steps), então só precisam entrar na config de replay.
const loadCrmOpportunitiesModal = () =>
  import(
    /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-opportunities-modal/crm-opportunities-modal.vue'
  )
const loadCrmScheduleTaskCalendarModal = () =>
  import(
    /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/schedule-task-calendar/crm-schedule-task-calendar.vue'
  )

// Teto de tentativas de replay por modal — ver `crmModalReplayAttempts`.
const CRM_MODAL_REPLAY_MAX_ATTEMPTS = 3

// Config declarativa do replay genérico (ver `replayCrmModalWhenChunkArrives`):
// cada entrada mapeia o evento do GlobalBus para o `ref` do template (sinal de
// que o modal real já montou e está ouvindo) e o método-seam usado para baixar
// o chunk (mesmo padrão de `loadTaskDetailModal`, que o unit test sobrescreve
// para controlar QUANDO o chunk resolve).
const CRM_MODAL_REPLAY_CONFIG = [
  {
    modalKey: MODAL.CRM_NEW_LEAD,
    refName: 'modalCRMAddNewLead',
    loaderMethod: 'loadCrmNewLeadModal',
  },
  {
    modalKey: MODAL.CRM_ADD_CLIENT,
    refName: 'modalCRMAddClient',
    loaderMethod: 'loadCrmAddClientModal',
  },
  {
    modalKey: MODAL.CRM_IMPORT_LEADS_BATCH,
    refName: 'crmImportLeadsBatch',
    loaderMethod: 'loadCrmImportLeadsBatchModal',
  },
  {
    modalKey: MODAL.TRANSCRIPT_PROJECT_EDIT,
    refName: 'transcriptProjectModal',
    loaderMethod: 'loadTranscriptProjectModal',
  },
  {
    modalKey: MODAL.CRM_OPPORTUNITIES,
    refName: 'modalCrmOpportunities',
    loaderMethod: 'loadCrmOpportunitiesModal',
  },
  {
    modalKey: MODAL.CRM_SCHEDULE_TASK_CALENDAR,
    refName: 'crmScheduleTaskCalendar',
    loaderMethod: 'loadCrmScheduleTaskCalendarModal',
  },
]

export default {
  name: 'GlobalModals',
  components: {
    ModalCarteiraRecomendada: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/dados-carteira-recomendada'
      ),
    ModalSelecionarCarteiraRecomendada: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/selecionar-carteira-recomendada'
      ),
    ModalContactWhitelabel: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/contact-whitelabel'
      ),
    ModalSelecionarCliente: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/select-client'),
    ModalAddPortfolioModelo: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/settings/portfolio-modelo/adicionar-modelo'
      ),
    ModalOpenClient: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/open-client'),
    ModalProduto: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/modal-produto'),
    ModalAlterarSenha: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/change-password'),
    ModalAcesso: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/acesso-cliente'),
    ModalPortabilidade: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/modal-portabilidade'
      ),
    ModalIPS: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/client-ips'),
    ModalChangeClass: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/settings/ativos/change-class'
      ),
    ModalIPSAnswerClient: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/ips/responder-cliente'
      ),
    ModalEditObservation: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/ips/edit-observation'
      ),
    ModalDadosPagamento: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/dados-pagamento'),
    ModalOpcoes: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/movimentacoes/opcoes-cadastro'
      ),
    ModalExportAccessClients: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/export/access-clients'
      ),
    ModalImportAccessClients: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/import/access-clients'
      ),
    ModalImportBaseAtivos: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/import/importar-base-ativos'
      ),
    ModalCreateRoom: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/workspace/create-room'
      ),
    ModalCreateTemplate: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/settings/email/adicionar-template-email'
      ),
    ModalSelectTemplate: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/select-template-email'
      ),
    ModalCotasHistoricas: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/import-manual-quotas'
      ),
    ModalVincularConta: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/vincular-conta'),
    ModalVincularAtivoEscolher: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/vincular-ativo-escolher'
      ),
    ModalRemoveLinkedAccounts: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/accounts/remove-linked'
      ),
    ModalAssinaturaProfissional: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/settings/email/adicionar-assinatura'
      ),
    ModalSeeProspect: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/mesa-digital/see-prospect'
      ),
    ModalEnviarEmail: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/send-email'),
    ModalPagarmeResgatar: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/pagarme-resgatar'),
    ModalPagarmeHistorico: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/pagarme-historico'
      ),
    ModalPagarmeCobrancaDetalhes: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/pagarme-detalhes-cobranca'
      ),
    ModalChangeCard: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/change-card'),
    ModalAddBankAccount: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/add-bank-account'),
    ModalAddBenchmark: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/settings/benchmark/add-benchmark'
      ),
    ModalCarteiraData: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/date-wallet'),
    ModalResumo: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/modal-resumo-cliente'
      ),
    ModalSelecionarModeloPDF: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/pdf-modelo/selecionar-modelo-pdf'
      ),
    ModalConsentirCrawlerDiario,
    ModalExportarExtrato: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/export/exportar-extrato-clientes'
      ),
    ModalImportarExtrato: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/import/importar-extrato-cliente'
      ),
    ModalComposicaoFundo: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/modal-composicao-fundo'
      ),
    ModalSaveFeeModel: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/portfolio/modal/portfolio-save-fee-model'
      ),
    ModalBatchFeeModel: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/portfolio/modal/batch-fee-model-clients'
      ),
    ModalMarkFeePaid: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/portfolio/modal/mark-fee-paid/mark-fee-paid.vue'
      ),
    ModalTeams: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/settings/modal/team/settings-team.vue'
      ),
    ModalNewProfessional: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/professionals/modal/professionals-new-professional'
      ),
    ModalProfessionalChurnList: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/professionals/modal/churn/churn-list/professionals-churn-list.vue'
      ),
    ModalProfessionalChurnRegister: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/professionals/modal/churn/churn-register/professionals-churn-register.vue'
      ),
    ModalBenchmarkComposition: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/settings/modal/settings-benchmark-composition.vue'
      ),
    ModalDownloadDre: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/finance/modal/finance-download-dre.vue'
      ),
    ModalFinanceDetailsComissionCategory: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/finance/modal/finance-details-comission-category.vue'
      ),
    ModalCalendarConfirmFinishTask: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/calendar/modal/confirm-finish-task/calendar-confirm-finish-task.vue'
      ),
    ModalCreateCalendar: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/calendar/modal/create-event/calendar-create-event.vue'
      ),
    AvatarUploadModal: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/professionals/modal/avatar-upload-modal.vue'
      ),
    MeusDados: () =>
      import(/* webpackChunkName: "global-modals" */ '@modal/meus-dados'),
    ModalExtensionToken,
    ModalWhatsappConectar,
    SuggestImprovement: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/suggest-improvement'
      ),
    ModalSelecionarModeloPDFCliente: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@/src/modal/pdf-modelo/modal-select-type-model-report.vue'
      ),
    ModalRegisterAsset: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/consolidator/modal/register-asset/consolidator-register-asset.vue'
      ),
    ModalAddInvestmentAccount: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/consolidator/modal/add-investment-account/consolidator-add-investment-account.vue'
      ),
    // Cadastro de aporte/resgate (movimentação). Registrado global para a ação
    // rápida da busca funcionar de qualquer página (fluxo SELECIONAR_CLIENTE ->
    // CADASTRAR_APORTE). created() só assina o bus — sem fetch no mount.
    ModalAporteResgate: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/salvar-aporte-resgate'
      ),
    GlobalSearch: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/global/modal/global-search/global-search.vue'
      ),
    // Palette da IA (Ctrl/Cmd+K dentro da `/ia`). Mora aqui pelo mesmo motivo do
    // GlobalSearch: a rota imersiva vai pelo shell v2 sem a lateral de módulos,
    // e este host é montado fora do switch de navegação.
    IaPalette: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/global/modal/ia-palette/ia-palette.vue'
      ),
    // Modal de créditos da Central de IA (Planos · Adicionar · Uso). Mora aqui pelo mesmo
    // motivo do palette: é aberto de dentro da rota imersiva do chat (shell v2 sem
    // lateral) — e precisa existir fora do switch de navegação.
    AiFrameworkCreditosModal: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/ai-framework/modal/creditos-modal/creditos-modal.vue'
      ),
    // Atalho global da visão imersiva do chat de IA (Ctrl/Cmd+I). Mesmo motivo
    // do GlobalSearch acima: precisa funcionar de QUALQUER página, e este host
    // é montado fora do switch de navegação.
    AtalhoIaImersiva: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/global/components/atalho-ia-imersiva/atalho-ia-imersiva.vue'
      ),
    // Antes só existia dentro de left-bar-actions (nav antiga); registrado aqui
    // para que a ação "Cadastrar cliente" da busca global funcione de qualquer
    // página (ex.: consolidador), onde left-bar-actions não está montado.
    ModalCadastroClientes: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modal/ModalCadastroClientes'
      ),
    AawzDriveRenameFile: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/aawz-drive/modal/aawz-drive-rename-file/aawz-drive-rename-file.vue'
      ),
    // Puramente GlobalBus (sem props) — antes importados/montados direto em
    // crm-lead-steps.vue e crm-oportunidade-motivos.vue, engordando o bundle
    // dessas telas. Movidos pra cá seguindo o mesmo padrão dos demais.
    CrmLeadPerdaModal: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/crm/modal/crm-lead-perda-modal/crm-lead-perda-modal.vue'
      ),
    CrmMotivoPerdaModal: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/crm/modal/crm-motivo-perda-modal/crm-motivo-perda-modal.vue'
        // Pastas de conversas do AAWZ Intelligence — global (não em conversations.vue)
        // porque esse componente pode montar 2x ao mesmo tempo (popup + página cheia),
        // o que duplicaria o listener do GlobalBus.
      ),
    IntelligenceV2FolderCreate: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/aawz-intelligence-v2/modal/folder-create/folder-create.vue'
      ),
    IntelligenceV2FolderRename: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/aawz-intelligence-v2/modal/folder-rename/folder-rename.vue'
      ),
    // spec-005/006/007 — curadoria de sugestões de IA, um único modal genérico
    // escopado por `{ tipo, id }` (reunião OU cliente). Global porque a tela
    // "Sugestões a triar" (aawz-intelligence-v2), o painel lateral de CRM >
    // Transcrições e as 4 telas de Relacionamento/Kanban de Leads disparam o
    // MESMO MODAL.IA_SUGGESTIONS/IA_SUGGESTION_EDIT/DECLINE — montar local em
    // cada view duplicaria o listener do GlobalBus (mesmo racional do
    // IntelligenceV2FolderCreate acima).
    IaSuggestionsModal: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/aawz-intelligence-v2/modal/ia-suggestions-modal/ia-suggestions-modal.vue'
      ),
    SuggestionEdit: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/aawz-intelligence-v2/modal/suggestion-edit/suggestion-edit.vue'
      ),
    SuggestionDecline: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/aawz-intelligence-v2/modal/suggestion-decline/suggestion-decline.vue'
      ),
    // Conectar as ferramentas SEM sair do chat (etapa 3 do onboarding,
    // 19/09/2026). Globais pelo mesmo motivo do `IntelligenceV2FolderCreate`
    // acima: o chat monta em DUAS superfícies ao mesmo tempo (popup + página
    // cheia), e montá-los dentro dele duplicaria o listener do GlobalBus — o
    // modal abriria duas vezes, uma por cima da outra.
    IntelligenceV2ConectarEmailAgenda: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/aawz-intelligence-v2/modal/conectar-email-agenda/conectar-email-agenda.vue'
      ),
    IntelligenceV2ConectarWhatsapp: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/aawz-intelligence-v2/modal/conectar-whatsapp/conectar-whatsapp.vue'
      ),
    IntelligenceV2ConectarWhatsappQrcode: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/aawz-intelligence-v2/modal/conectar-whatsapp-qrcode/conectar-whatsapp-qrcode.vue'
      ),
    // Advisory Hub (spec-011, D-3 do design it.2) — registrado aqui (e não
    // importado direto em `view-advisory-engagement.vue`) porque o gate
    // `design-system:no-regression` marca qualquer tag `*Modal` invocada
    // dentro de um `views/*.vue` como "modal inline detectado", mesmo com a
    // tag no fim do template — o mesmo padrão dos outros modais deste bloco.
    AdvisoryDecisaoModal: () =>
      import(
        /* webpackChunkName: "global-modals" */ '@modules/aawz-intelligence-v2/modal/advisory-decisao-modal/advisory-decisao-modal.vue'
      ),
    // Modais do CRM em chunk PRÓPRIO ("global-modals-crm"): são pesados (o
    // crm-lead-steps arrasta todas as tabs do cliente) e não fazia sentido
    // engordar o chunk dos outros ~60. Ver bloco no template para o racional
    // do host único.
    CrmLeadSteps: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/lead-steps/crm-lead-steps.vue'
      ),
    CrmRegisterRelationshipModal: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-register-relationship-modal/crm-register-relationship-modal.vue'
      ),
    TranscriptDetailModal,
    // Alcançáveis pela busca global (ações "Criar oportunidade"/"Agendar
    // tarefa") de fora do módulo CRM — mesmo risco de chunk frio dos 4
    // loaders acima, protegidos pela mesma CRM_MODAL_REPLAY_CONFIG.
    ModalCrmOpportunities: loadCrmOpportunitiesModal,
    CrmOportunidadePerdaModal: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-oportunidade-perda-modal/crm-oportunidade-perda-modal.vue'
      ),
    CrmScheduleTaskCalendar: loadCrmScheduleTaskCalendarModal,
    // Cadastro de lead e cadastro de cliente do CRM. Registrados aqui para que
    // as ações "Cadastrar lead"/"Adicionar cliente" da busca global funcionem
    // de qualquer página, não só dentro do shell do CRM (crm-nav-bar, onde
    // viviam antes) — sem host global,
    // GlobalBus.$emit(MODAL.CRM_NEW_LEAD/CRM_ADD_CLIENT) disparado de fora do
    // CRM não tinha ouvinte.
    ModalCRMAddNewLead: loadCrmNewLeadModal,
    ModalCRMAddClient: loadCrmAddClientModal,
    // Importação/edição em lote de leads e clientes (planilha). Antes vivia só
    // em crm-nav-bar.vue; os gatilhos (view-crm-leads-tabs, view-crm-relationship)
    // já emitiam só via GlobalBus MODAL.CRM_IMPORT_LEADS_BATCH — sem props —
    // então a migração para o host global não muda nenhum call-site.
    CrmImportLeadsBatch: loadCrmImportLeadsBatchModal,
    // Criação/edição de projeto de transcrição. Antes vivia só em
    // view-crm-transcricoes.vue (shell das 4 abas); sem props, aberto via
    // GlobalBus MODAL.TRANSCRIPT_PROJECT_EDIT pelos gatilhos em
    // view-crm-transcricoes-projects.vue e transcript-projects.vue.
    TranscriptProjectModal: loadTranscriptProjectModal,
    // Detalhe de tarefa: aberto do CRM, do fee-fixo, do módulo de Tarefas, do
    // atendimento e do card de Tarefas do perfil do cliente — ou seja, de
    // praticamente qualquer rota. Também vem para cá em vez de ser montado em
    // cada host.
    TaskDetailModal: loadTaskDetailModal,
    // Convite para avisar o solicitante do WS ao concluir um card da esteira
    // Backoffice. Vem para cá porque tem mais de um host — Kanban e Lista de
    // Tarefas abrem o MESMO modal, e montá-lo nos dois faria o convite aparecer
    // duplicado em quem renderiza as duas views na mesma rota.
    SolicitacaoAvisoConclusaoModal: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/solicitacoes/modal/solicitacao-aviso-conclusao-modal/solicitacao-aviso-conclusao-modal.vue'
      ),
    // Satélites que o próprio crm-lead-steps (e o lead-summary-card dentro dele)
    // abrem. Ficavam só no crm-nav-bar, então no perfil do cliente o lápis do
    // resumo e o "comentar" da timeline não faziam nada — no-op silencioso.
    CrmEditFieldModal: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-edit-field-modal/crm-edit-field-modal.vue'
      ),
    ModalEditValueTime: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/edit-value-time/crm-edit-value-time.vue'
      ),
    ModalClientReguaLink: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-client-regua-link-modal/crm-client-regua-link-modal.vue'
      ),
    CrmEditFeeModel: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-edit-fee-model/crm-edit-fee-model.vue'
      ),
    // Antes montado localmente em view-crm-calendar.vue e em
    // view-crm-relationship-priorities.vue (origens 'calendario' e
    // 'prioridades'). Ganhou uma 3ª origem, 'cliente' (aba Tarefas do detalhe
    // do cliente, dentro de CrmLeadSteps acima) — como CrmLeadSteps já é
    // global, manter as duas montagens locais faria o modal abrir 2x em quem
    // navegasse por Calendário/Prioridades com o detalhe do cliente aberto
    // (dois listeners de MODAL.CRM_EDIT_DATETIME). Movido para cá seguindo o
    // mesmo racional dos demais satélites desta seção.
    CrmEditDatetime: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-edit-datetime/crm-edit-datetime.vue'
      ),
    SummaryLayoutForm: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/settings/modal/crm/summary-layout-form/summary-layout-form.vue'
      ),
    CrmCommentModal: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-comment-modal/crm-comment-modal.vue'
      ),
    CampoObrigatorioPicker: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/campo-obrigatorio-picker/campo-obrigatorio-picker.vue'
      ),
    CrmRequiredFieldsModal: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-required-fields-modal/crm-required-fields-modal.vue'
      ),
    // Ampliar (crm-evolucao): extraído de BaseModal inline. crm-evolucao é
    // reusado por crm-portfolio-benchmark E patrimonio-consolidated, que podem
    // aparecer juntos na mesma tela (Visão Geral) — montar local duplicaria o
    // listener e abriria dois dialogs empilhados no mesmo clique.
    CrmEvolucaoAmpliarModal: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-evolucao-ampliar-modal/crm-evolucao-ampliar-modal.vue'
      ),
    // Resultado/reagendamento de régua (crm-register-relationship-modal):
    // extraído de v-dialog inline. Puramente local ao modal pai (nunca aberto
    // fora dele) — mora aqui só porque o pai já está neste host único.
    CrmRegisterRelationshipResultModal: () =>
      import(
        /* webpackChunkName: "global-modals-crm" */ '@modules/crm/modal/crm-register-relationship-result-modal/crm-register-relationship-result-modal.vue'
      ),
  },
  data() {
    return {
      GlobalBus,
      // Um replay de MODAL.TASK_DETAIL em voo (ver
      // `replayTaskDetailWhenChunkArrives`). Sem isto, dois emits durante a
      // mesma janela de download do chunk viram dois replays.
      taskDetailReplayPending: false,
      // Mesma trava, generalizada por modal (ver `replayCrmModalWhenChunkArrives`
      // e `CRM_MODAL_REPLAY_CONFIG`).
      crmModalReplayPending: CRM_MODAL_REPLAY_CONFIG.reduce(
        (acc, { modalKey }) => {
          acc[modalKey] = false
          return acc
        },
        {}
      ),
      // Corta um loop teórico: se o `ref` continuar falsy mesmo depois do
      // chunk resolver (ex.: `v-if` escondendo o modal por algum motivo), o
      // replay re-emite, o próprio handler eager do host escuta de novo e
      // tentaria repetir para sempre. Acima de CRM_MODAL_REPLAY_MAX_ATTEMPTS
      // o replay desiste em silêncio (o emit original já chegou a quem
      // estava ouvindo; só a tentativa de replay é abandonada).
      crmModalReplayAttempts: CRM_MODAL_REPLAY_CONFIG.reduce(
        (acc, { modalKey }) => {
          acc[modalKey] = 0
          return acc
        },
        {}
      ),
    }
  },
  created() {
    // spec-007 — o modal genérico `IaSuggestionsModal` não conhece CRM (não
    // importa `atualizarClienteNoRelacionamentoOtimista`/`atualizarClienteNosLeads`
    // diretamente, senão voltaria a ser CRM-specific). Este componente já é o
    // host global de modais (mesmo racional documentado acima para os modais
    // com múltiplos emitters) — companion listener aqui propaga a contagem
    // fresca de pendentes só quando `scope.tipo === 'cliente'`; para
    // `scope.tipo === 'transcript'` não faz nada (a view de Transcrições cuida
    // do próprio patch local via seu próprio listener do mesmo evento).
    GlobalBus.$on(MODAL.IA_SUGGESTIONS_CHANGED, this.onIaSuggestionsChanged)
    // Assinatura EAGER do host (o modal em si continua async) — ver
    // `replayTaskDetailWhenChunkArrives`.
    GlobalBus.$on(MODAL.TASK_DETAIL, this.replayTaskDetailWhenChunkArrives)

    // Mesma assinatura eager, generalizada para os modais do CRM alcançáveis
    // pela busca global fora do módulo CRM (config em CRM_MODAL_REPLAY_CONFIG,
    // hoje 6 entradas). Guarda a referência do handler (closure por modalKey)
    // para poder dar `$off` simétrico em beforeDestroy.
    this._crmModalReplayHandlers = CRM_MODAL_REPLAY_CONFIG.map(
      ({ modalKey }) => {
        const handler = (payload) =>
          this.replayCrmModalWhenChunkArrives(modalKey, payload)
        GlobalBus.$on(modalKey, handler)
        return { modalKey, handler }
      }
    )

    // Instala o subscriber de mutations do modulo Tarefas (historico do CRM).
    // Fica aqui porque o TaskDetailModal e global — criar tarefa fora do CRM
    // tambem precisa registrar. Import DINAMICO: estatico arrastaria apiTarefas
    // (1.7k linhas) + as APIs de historico para o chunk do layout autenticado,
    // e o subscriber so precisa existir antes do primeiro ADD_TASK.
    import(
      /* webpackChunkName: "global-modals-crm" */ '@modules/crm/adapters/crmTasksAdapter'
    ).catch(() => {})
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.IA_SUGGESTIONS_CHANGED, this.onIaSuggestionsChanged)
    GlobalBus.$off(MODAL.TASK_DETAIL, this.replayTaskDetailWhenChunkArrives)
    this._crmModalReplayHandlers.forEach(({ modalKey, handler }) => {
      GlobalBus.$off(modalKey, handler)
    })
  },
  methods: {
    // Seam de teste: envolve o factory hoistado para que o unit test possa
    // controlar QUANDO o chunk resolve (a corrida real depende da rede).
    loadTaskDetailModal() {
      return loadTaskDetailModal()
    },
    /**
     * Deep-link de tarefa: entrega o payload mesmo quando o chunk async chega
     * depois do $emit.
     *
     * `TaskDetailModal` mora no chunk "global-modals-crm" (o mais pesado) e só
     * assina MODAL.TASK_DETAIL no created() do PRÓPRIO modal — ou seja, depois
     * do download+parse. O bootstrap de deep-link do board
     * (`view-task-board._openDeepLinkedTask`) emite ainda no created() da view,
     * logo após duas chamadas de rede. Com o chunk em cache o modal já ouve e a
     * tarefa abre; em cache frio (primeiro acesso, deploy novo, rede lenta) o
     * emit ganha a corrida, o GlobalBus não enfileira e o MESMO link não abre
     * nada — o sintoma "às vezes abre, às vezes não".
     *
     * O host assina eager (custo zero: já está no bundle inicial), guarda o
     * payload e re-emite quando o chunk resolve. Alternativa descartada: tornar
     * o modal eager como as exceções do topo do arquivo — custaria o modal mais
     * pesado no caminho crítico de TODO boot para consertar um caso de borda.
     *
     * O `$refs` é o sinal de que o modal real já montou (logo, já está ouvindo):
     * no caminho normal saímos aqui, e é também o que corta o loop do replay.
     */
    replayTaskDetailWhenChunkArrives(payload) {
      if (this.$refs.taskDetailModal) return
      if (this.taskDetailReplayPending) return
      this.taskDetailReplayPending = true
      this.loadTaskDetailModal()
        .then(() => this.$nextTick())
        .then(() => {
          this.taskDetailReplayPending = false
          GlobalBus.$emit(MODAL.TASK_DETAIL, payload)
        })
        .catch(() => {
          // Chunk indisponível (deploy trocou o hash, rede caiu): libera a
          // trava para que um próximo emit tente de novo em vez de ficar mudo.
          this.taskDetailReplayPending = false
        })
    },
    // Seams de teste, mesmo racional de `loadTaskDetailModal`: envolvem os
    // factories hoistados para que o unit test possa controlar QUANDO cada
    // chunk resolve (a corrida real depende da rede).
    loadCrmNewLeadModal() {
      return loadCrmNewLeadModal()
    },
    loadCrmAddClientModal() {
      return loadCrmAddClientModal()
    },
    loadCrmImportLeadsBatchModal() {
      return loadCrmImportLeadsBatchModal()
    },
    loadTranscriptProjectModal() {
      return loadTranscriptProjectModal()
    },
    loadCrmOpportunitiesModal() {
      return loadCrmOpportunitiesModal()
    },
    loadCrmScheduleTaskCalendarModal() {
      return loadCrmScheduleTaskCalendarModal()
    },
    /**
     * Generalização de `replayTaskDetailWhenChunkArrives` para os modais do
     * CRM que a busca global (Ctrl/Cmd+K) pode abrir de fora do módulo CRM.
     * Mesmo mecanismo: o host assina eager (custo zero, já está no bundle
     * inicial), guarda o payload perdido e re-emite quando o chunk
     * "global-modals-crm" resolve e o modal real efetivamente monta (o `ref`
     * no template é o sinal de que ele já está ouvindo).
     */
    replayCrmModalWhenChunkArrives(modalKey, payload) {
      const config = CRM_MODAL_REPLAY_CONFIG.find(
        (c) => c.modalKey === modalKey
      )
      if (!config) return
      if (this.$refs[config.refName]) return
      if (this.crmModalReplayPending[modalKey]) return
      if (this.crmModalReplayAttempts[modalKey] >= CRM_MODAL_REPLAY_MAX_ATTEMPTS) {
        return
      }
      this.crmModalReplayAttempts[modalKey] += 1
      this.crmModalReplayPending[modalKey] = true
      this[config.loaderMethod]()
        .then(() => this.$nextTick())
        .then(() => {
          this.crmModalReplayPending[modalKey] = false
          GlobalBus.$emit(modalKey, payload)
        })
        .catch(() => {
          this.crmModalReplayPending[modalKey] = false
        })
    },
    onIaSuggestionsChanged({ scope, pendentes } = {}) {
      if (!scope || scope.tipo !== 'cliente' || !scope.id) return
      const patch = {
        idClienteApp: scope.id,
        clienteData: {
          possui_sugestao_ia: pendentes > 0,
          qtd_sugestoes_ia: pendentes,
        },
      }
      atualizarClienteNoRelacionamentoOtimista(patch)
      atualizarClienteNosLeads(patch)
    },
  },
}
</script>

<template>
  <div class="global-modals">
    <MeusDados :bus="GlobalBus" />
    <ModalExtensionToken />
    <ModalWhatsappConectar />
    <ModalCarteiraRecomendada />
    <ModalSelecionarCarteiraRecomendada />
    <ModalContactWhitelabel />
    <ModalAlterarSenha />
    <ModalSelecionarCliente :bus="GlobalBus" />
    <ModalAddPortfolioModelo />
    <ModalOpenClient />
    <ModalIPS />
    <ModalChangeClass />
    <ModalIPSAnswerClient />
    <ModalEditObservation />
    <ModalOpcoes />
    <SuggestImprovement :bus="GlobalBus" />
    <ModalProduto />
    <ModalAcesso />
    <ModalExportAccessClients />
    <ModalImportAccessClients />
    <ModalImportBaseAtivos />
    <ModalCotasHistoricas />
    <ModalVincularAtivoEscolher />
    <ModalRegisterAsset />
    <ModalAddInvestmentAccount />
    <ModalAporteResgate :bus="GlobalBus" />
    <GlobalSearch />
    <IaPalette />
    <AiFrameworkCreditosModal />
    <AtalhoIaImersiva />
    <ModalCadastroClientes />
    <ModalRemoveLinkedAccounts />
    <ModalAssinaturaProfissional />
    <ModalSeeProspect />
    <ModalEnviarEmail />
    <ModalPagarmeResgatar />
    <ModalPagarmeHistorico />
    <ModalPagarmeCobrancaDetalhes />
    <ModalAddBankAccount />
    <ModalAddBenchmark />
    <ModalChangeCard />
    <ModalPortabilidade />
    <ModalVincularConta />
    <ModalDadosPagamento />
    <ModalResumo />
    <ModalCreateRoom />
    <ModalCreateTemplate />
    <ModalSelectTemplate />
    <ModalCarteiraData />
    <ModalSelecionarModeloPDF />
    <ModalSelecionarModeloPDFCliente />
    <ModalConsentirCrawlerDiario />
    <ModalExportarExtrato />
    <ModalImportarExtrato />
    <ModalComposicaoFundo />
    <ModalSaveFeeModel />
    <ModalBatchFeeModel />
    <ModalMarkFeePaid />
    <ModalTeams />
    <ModalNewProfessional />
    <ModalProfessionalChurnList />
    <ModalProfessionalChurnRegister />

    <ModalBenchmarkComposition />
    <ModalDownloadDre />
    <ModalFinanceDetailsComissionCategory />
    <ModalCalendarConfirmFinishTask />
    <ModalCreateCalendar />
    <AvatarUploadModal />
    <AawzDriveRenameFile />
    <CrmLeadPerdaModal />
    <CrmMotivoPerdaModal />
    <IntelligenceV2FolderCreate />
    <IntelligenceV2FolderRename />
    <IaSuggestionsModal />
    <SuggestionEdit />
    <SuggestionDecline />
    <AdvisoryDecisaoModal />
    <IntelligenceV2ConectarEmailAgenda />
    <IntelligenceV2ConectarWhatsapp />
    <IntelligenceV2ConectarWhatsappQrcode />

    <!-- Modais do CRM. Antes eram declarados duas vezes, em crm-nav-bar e em
         fee-fixo-nav-bar — shells de rota mutuamente exclusivos, então não havia
         dupla montagem em runtime, mas havia código duplicado e, principalmente,
         nenhum ouvinte para quem emitisse os mesmos eventos fora dessas duas
         rotas (perfil do cliente na navegação v2, painel de atendimento). Aqui
         ficam montados uma vez só, para toda a plataforma — e como agora este
         host coexiste com qualquer rota, remontá-los numa view passaria a causar
         dupla resposta ao mesmo evento (há teste travando isso). Os três últimos
         são satélites abertos de dentro do crm-lead-steps. -->
    <CrmLeadSteps />
    <CrmRegisterRelationshipModal />
    <TranscriptDetailModal />
    <ModalCrmOpportunities ref="modalCrmOpportunities" />
    <CrmOportunidadePerdaModal />
    <CrmScheduleTaskCalendar ref="crmScheduleTaskCalendar" />
    <ModalCRMAddNewLead ref="modalCRMAddNewLead" />
    <ModalCRMAddClient ref="modalCRMAddClient" />
    <CrmImportLeadsBatch ref="crmImportLeadsBatch" />
    <TranscriptProjectModal ref="transcriptProjectModal" />
    <TaskDetailModal ref="taskDetailModal" />
    <SolicitacaoAvisoConclusaoModal />
    <CrmEditFieldModal />
    <ModalEditValueTime />
    <ModalClientReguaLink />
    <CrmEditFeeModel />
    <CrmEditDatetime />
    <SummaryLayoutForm />
    <CrmCommentModal />
    <CampoObrigatorioPicker />
    <CrmRequiredFieldsModal />
    <CrmEvolucaoAmpliarModal />
    <CrmRegisterRelationshipResultModal />
  </div>
</template>
